import type { OptionsMap } from '@openx/types/options.ts';
import type { RequiredTargetingFormType } from '@openx/types/targeting/targetingPresets.ts';
import { useMemo } from 'react';
import { useTargetingContext } from '../../../utils/context/TargetingContext.tsx';
import { RequiredTargetingSectionKeys, TargetingElementKeys } from '../context/types.ts';
import { useDeviceInitialState } from '../hooks/initialState/useDeviceInitialState.tsx';
import { useReadOnlyDeviceInitialState } from '../hooks/readOnlyInitialState/useReadOnlyDeviceInitialState.tsx';
import { useSyncInitialTargeting } from '../hooks/useSyncInitialTargeting.ts';
import { TargetingPresetSection } from '../TargetingPresetSection/TargetingPresetSection.tsx';

type DeviceSectionProps = {
  targetingParams?: RequiredTargetingFormType | null;
  readOnly?: boolean;
};

export const DeviceSection = ({ readOnly, targetingParams }: DeviceSectionProps) => {
  const { useDeviceTypesOptionsFetch } = useTargetingContext();
  const { desktop_devices, mobile_devices, tv_devices } = useDeviceTypesOptionsFetch();

  const deviceTypes: OptionsMap = useMemo(() => {
    const hasMobile = Object.keys(mobile_devices.data).length > 0;
    const hasTV = Object.keys(tv_devices.data).length > 0;
    const mobileCategoryOptions = {
      [TargetingElementKeys.DEVICE_MOBILE]: {
        id: TargetingElementKeys.DEVICE_MOBILE,
        name: '',
      },
      ...mobile_devices.data,
    };
    const tvCategoryOptions = {
      [TargetingElementKeys.DEVICE_TV]: {
        id: TargetingElementKeys.DEVICE_TV,
        name: '',
      },
      ...tv_devices.data,
    };

    return {
      ...desktop_devices.data,
      ...(hasMobile ? mobileCategoryOptions : {}),
      ...(hasTV ? tvCategoryOptions : {}),
    };
  }, [desktop_devices.data, mobile_devices.data, tv_devices.data]);

  const isDeviceTypesLoading = useMemo(
    () => desktop_devices.isLoading || mobile_devices.isLoading || tv_devices.isLoading,
    [desktop_devices.isLoading, mobile_devices.isLoading, tv_devices.isLoading]
  );

  const readOnlyInitialState = useReadOnlyDeviceInitialState(targetingParams);
  const editableInitialState = useDeviceInitialState();
  const initialState = useMemo(
    () => (readOnly ? readOnlyInitialState : editableInitialState),
    [readOnly, editableInitialState, readOnlyInitialState]
  );

  useSyncInitialTargeting({
    isLoading: isDeviceTypesLoading,
    sectionKey: RequiredTargetingSectionKeys.DEVICE,
    initialState,
    externalOptionsMap: deviceTypes,
  });

  return (
    <TargetingPresetSection
      name="device_type"
      sectionKey={RequiredTargetingSectionKeys.DEVICE}
      label={initialState.label}
      elements={initialState.elements}
      isLoading={isDeviceTypesLoading}
      readOnly={!!readOnly}
    />
  );
};
